import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    Loader2, ArrowLeft, Copy, CheckCircle2, Receipt, XCircle,
    Wallet, ShieldCheck, Timer, ListChecks, Sun, Moon, Info, AlertTriangle, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    useConferenceOutputBillingByToken,
    useCheckOutputBillingStatus,
} from "@/services/conferenceOutputPublicService";
import PaymentGuide from "@/components/public/conference-payment/PaymentGuide";

export default function ConferenceOutputBillingPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();

    const [copied, setCopied] = useState(false);
    const [timeLeft, setTimeLeft] = useState("");
    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme === "dark") {
            setIsDark(true);
            document.documentElement.classList.add("dark");
        }
    }, []);

    const toggleDarkMode = () => {
        const newMode = !isDark;
        setIsDark(newMode);
        document.documentElement.classList.toggle("dark", newMode);
        localStorage.setItem("theme", newMode ? "dark" : "light");
    };

    const { data: billing, isLoading, isError } = useConferenceOutputBillingByToken(token);
    const checkStatus = useCheckOutputBillingStatus(token);

    const handleCheckStatus = () => {
        checkStatus.mutate(undefined, {
            onSuccess: (data) => {
                if (data?.status === "paid") {
                    toast.success("Pembayaran berhasil dikonfirmasi!");
                } else if (data?.status === "expired") {
                    toast.error("Tagihan sudah kedaluwarsa.");
                } else {
                    toast("Belum ada pembayaran terdeteksi. Coba cek lagi beberapa saat setelah transfer.", { icon: <Info className="w-5 h-5 text-blue-500" /> });
                }
            },
            onError: (error: any) => {
                toast.error(error?.response?.data?.message || "Gagal memeriksa status pembayaran. Silakan coba lagi.");
            },
        });
    };

    useEffect(() => {
        if (!billing?.expired_at_payment || billing.status !== "unpaid") return;

        const calculateTimeLeft = () => {
            const difference = new Date(billing.expired_at_payment!).getTime() - new Date().getTime();
            if (difference <= 0) return "Expired";
            const days = Math.floor(difference / (1000 * 60 * 60 * 24));
            const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((difference / 1000 / 60) % 60);
            const seconds = Math.floor((difference / 1000) % 60);
            let timeString = "";
            if (days > 0) timeString += `${days}h `;
            timeString += `${hours.toString().padStart(2, "0")}j ${minutes.toString().padStart(2, "0")}m ${seconds.toString().padStart(2, "0")}d`;
            return timeString;
        };

        setTimeLeft(calculateTimeLeft());
        const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
        return () => clearInterval(timer);
    }, [billing]);

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const formatCurrency = (amount: string | number, currency: string) =>
        new Intl.NumberFormat(currency === "USD" ? "en-US" : "id-ID", { style: "currency", currency, minimumFractionDigits: 0 }).format(Number(amount));

    const formatDate = (isoString: string) =>
        new Intl.DateTimeFormat("en-US", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(isoString));

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600 mb-4" />
                <p className="text-slate-500 dark:text-slate-400 font-medium tracking-wide">Memuat informasi tagihan...</p>
            </div>
        );
    }

    if (isError || !billing) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 p-4 text-center transition-colors">
                <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-6">
                    <Receipt className="w-10 h-10" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Tagihan Tidak Ditemukan</h1>
                <p className="text-slate-500 dark:text-slate-400 max-w-md mb-8">Link tagihan ini tidak valid, atau proses registrasi belum sampai tahap pembayaran.</p>
                <Button onClick={() => navigate("/")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 h-12 font-bold">Kembali ke Beranda</Button>
            </div>
        );
    }

    const { status, currency, amount } = billing;
    const adminFee = currency === "USD" ? 5 : 2500;
    const totalWithFee = Number(amount) + adminFee;

    let headerConfig = { bgClass: "bg-blue-600 dark:bg-blue-900", statusText: "MENUNGGU PEMBAYARAN", Icon: Wallet };
    if (status === "paid") headerConfig = { bgClass: "bg-emerald-600 dark:bg-emerald-700", statusText: "PEMBAYARAN BERHASIL", Icon: ShieldCheck };
    else if (status === "expired") headerConfig = { bgClass: "bg-slate-600 dark:bg-slate-800", statusText: "TAGIHAN KEDALUWARSA", Icon: XCircle };
    else if (status === "cancelled") headerConfig = { bgClass: "bg-red-600 dark:bg-red-700", statusText: "DIBATALKAN", Icon: XCircle };

    return (
        <div className="min-h-screen bg-[#FAF9F6] dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 py-8 px-4 transition-colors relative">
            <div className="max-w-3xl mx-auto space-y-6">
                <div className="flex items-center justify-between mb-6">
                    <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors">
                        <ArrowLeft className="w-4 h-4" /> Kembali ke Beranda
                    </button>
                    <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="rounded-full w-10 h-10 bg-slate-200/50 hover:bg-slate-300/50 dark:bg-slate-800 dark:hover:bg-slate-700 shrink-0 text-slate-700 dark:text-slate-200 transition-colors">
                        {isDark ? <Sun className="h-4 w-4 text-yellow-400" /> : <Moon className="h-4 w-4" />}
                    </Button>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <div className={`p-6 md:p-8 text-white relative overflow-hidden flex items-center gap-4 ${headerConfig.bgClass}`}>
                        <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
                        <div className="relative z-10 bg-white/20 p-3.5 rounded-xl backdrop-blur-sm border border-white/20 shrink-0">
                            <headerConfig.Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                        </div>
                        <div className="relative z-10 flex-1 flex flex-col md:flex-row md:items-center justify-between gap-2">
                            <div>
                                <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest mb-0.5">ID Customer</p>
                                <p className="text-base font-bold tracking-widest">{billing.customer_number || "-"}</p>
                            </div>
                            <div className="md:text-right">
                                <p className="text-white/80 text-[10px] uppercase tracking-wider font-bold mb-0.5">Status</p>
                                <h1 className="text-lg sm:text-xl font-black tracking-tight uppercase drop-shadow-sm">{headerConfig.statusText}</h1>
                            </div>
                        </div>
                    </div>

                    {status === "expired" && (
                        <div className="bg-slate-50/30 dark:bg-slate-900/50 p-8 text-center flex flex-col items-center">
                            <XCircle className="w-14 h-14 text-slate-400 mb-4" />
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Tagihan Kedaluwarsa</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md leading-relaxed">Batas waktu pembayaran sudah lewat. Silakan hubungi panitia untuk perpanjangan.</p>
                        </div>
                    )}

                    {status === "paid" && (
                        <div className="bg-slate-50/30 dark:bg-slate-900/50 p-8 text-center flex flex-col items-center">
                            <div className="w-20 h-20 bg-green-50 dark:bg-green-900/20 text-green-500 rounded-full flex items-center justify-center mb-4">
                                <CheckCircle2 className="w-10 h-10" />
                            </div>
                            <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Pembayaran Terkonfirmasi!</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Terima kasih. Pembayaran biaya publikasi prosiding Anda telah kami terima.</p>
                        </div>
                    )}

                    {status === "unpaid" && billing.invoice_number && (
                        <div className="flex flex-col">
                            <div className="p-6 md:p-10 bg-slate-50/30 dark:bg-slate-900/50 flex flex-col items-center text-center border-b border-slate-100 dark:border-slate-800">
                                <div>
                                    <p className="text-slate-500 dark:text-slate-400 text-[11px] font-bold uppercase tracking-widest mb-1">Total Pembayaran</p>
                                    <h2 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tighter">
                                        {formatCurrency(billing.amount, billing.currency)}
                                    </h2>
                                    {billing.expired_at_payment && timeLeft !== "Expired" && (
                                        <div className="mt-3 flex flex-wrap justify-center items-center gap-2 text-xs font-medium text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 py-1.5 px-3.5 rounded-lg border border-orange-100 dark:border-orange-900/50 w-fit mx-auto">
                                            <Timer className="w-3.5 h-3.5 animate-pulse" />
                                            <span>Berakhir dalam: <span className="font-bold font-mono tracking-wider">{timeLeft}</span></span>
                                            <span className="opacity-50 hidden sm:inline">•</span>
                                            <span className="hidden sm:inline">Bayar sebelum {formatDate(billing.expired_at_payment)}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="w-full mt-8 bg-white dark:bg-slate-800 rounded-2xl p-4 md:p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
                                    <p className="text-slate-500 dark:text-slate-400 text-[11px] font-bold uppercase tracking-widest mb-3 text-left pl-1">Nomor Virtual Account</p>
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div className="flex-1 min-w-0 w-full text-center sm:text-left px-1 overflow-hidden">
                                            <span className="text-2xl md:text-3xl font-black text-blue-700 dark:text-blue-400 font-mono tracking-widest whitespace-nowrap select-all block truncate">
                                                {billing.invoice_number}
                                            </span>
                                        </div>
                                        <Button onClick={() => handleCopy(billing.invoice_number || "")} className="rounded-xl h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm w-full sm:w-auto shrink-0 shadow-md">
                                            {copied ? <><CheckCircle2 className="w-4 h-4 mr-2" /> TERSALIN</> : <><Copy className="w-4 h-4 mr-2" /> SALIN NOMOR</>}
                                        </Button>
                                    </div>
                                </div>

                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCheckStatus}
                                    disabled={checkStatus.isPending}
                                    className="mt-4 rounded-xl border-slate-300 dark:border-slate-700"
                                >
                                    {checkStatus.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                    Cek Status Pembayaran
                                </Button>
                            </div>

                            <div className="p-6 md:p-10 bg-white dark:bg-slate-900">
                                <div className="space-y-5">
                                    <div className="flex items-center gap-3 text-emerald-700 dark:text-emerald-400">
                                        <Info className="w-5 h-5" />
                                        <h4 className="font-bold text-base">Informasi Pembayaran ({currency})</h4>
                                    </div>
                                    <div className="bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/60 dark:border-amber-800/30 rounded-2xl p-5 flex gap-3 shadow-sm">
                                        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                        <div className="text-sm text-amber-900 dark:text-amber-200/90 w-full">
                                            <p className="font-bold mb-2">Penting: Transfer dari Bank Selain BNI</p>
                                            <p className="mb-3">
                                                Jika Anda transfer menggunakan bank <strong>selain BNI</strong>, wajib menginput <strong>Nominal Tagihan + Fee Admin VA ({formatCurrency(adminFee, currency)})</strong> secara manual.
                                            </p>
                                            <div className="bg-white/80 dark:bg-slate-900/50 p-4 rounded-xl border border-amber-100 dark:border-amber-900/50">
                                                <ul className="space-y-1 font-mono text-sm">
                                                    <li className="flex justify-between gap-4">
                                                        <span>Nominal Tagihan:</span>
                                                        <span>{formatCurrency(amount, currency)}</span>
                                                    </li>
                                                    <li className="flex justify-between gap-4 border-b border-amber-100 dark:border-amber-800/50 pb-1.5">
                                                        <span>Fee Admin VA BNI:</span>
                                                        <span>{formatCurrency(adminFee, currency)} +</span>
                                                    </li>
                                                    <li className="flex justify-between gap-4 font-bold text-blue-700 dark:text-blue-400 pt-1.5">
                                                        <span>Total Diinput:</span>
                                                        <span>{formatCurrency(totalWithFee, currency)}</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {billing.registration?.decision && (
                    <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden mb-6">
                        <div className="bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 p-6 flex justify-between items-center">
                            <h3 className="font-bold text-xs uppercase tracking-widest text-white opacity-90 flex items-center gap-2">
                                <ListChecks className="w-4 h-4" /> Rincian Registrasi
                            </h3>
                        </div>
                        <div className="p-6 md:p-8 bg-slate-50/30 dark:bg-slate-900/50 space-y-4">
                            <div className="flex justify-between items-center pb-4 border-b border-slate-100 dark:border-slate-800">
                                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Publisher</span>
                                <span className="font-bold text-sm text-slate-900 dark:text-white">{billing.fee?.publisher || billing.registration.decision.publisher || "-"}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Track</span>
                                <span className="font-bold text-sm text-slate-900 dark:text-white">{billing.registration.decision.conference?.name || "-"}</span>
                            </div>
                        </div>
                    </div>
                )}

                {status === "unpaid" && billing.invoice_number && (
                    <div className="mb-8">
                        <PaymentGuide />
                    </div>
                )}

                <div className="text-center text-xs text-slate-400 dark:text-slate-500 space-y-1 pb-10">
                    {status === "unpaid" ? <p>Jika Anda sudah melakukan pembayaran, status akan diperbarui otomatis.</p> : null}
                    <p>Jika ada kendala, silakan hubungi panitia via WhatsApp.</p>
                </div>
            </div>
        </div>
    );
}
